/**
 * useAudioUpload Hook
 *
 * Single Responsibility: Handle audio file uploads with metadata extraction
 * Open/Closed: Extensible through callbacks, closed for modification
 * Dependency Inversion: Depends on AudioMetadata abstraction, not implementation
 */

import { useCallback } from 'react';

import { useAudioMetadata } from './useAudioMetadata';
import { type AudioMetadataResult, isAudioBlob } from '../utils/audioFormats';

export interface AudioUploadResult extends AudioMetadataResult {
  /** Original file name */
  fileName: string;
  /** File size in bytes */
  fileSize: number;
}

export interface UseAudioUploadOptions {
  /** Maximum file size in bytes (default: 100MB) */
  maxFileSize?: number;
  /** Callback when upload fails */
  onError?: (error: string) => void;
  /** Callback when upload succeeds */
  onSuccess?: (result: AudioUploadResult) => void;
  /** Validate file before processing */
  validateFile?: (file: File) => string | null;
}

export interface UseAudioUploadReturn {
  /** Current error message */
  error: string | null;
  /** Current metadata extraction state */
  isLoading: boolean;
  /** Reset state */
  reset: () => void;
  /** Upload and process audio file */
  upload: (file: File) => Promise<AudioUploadResult | null>;
}

const DEFAULT_MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB

/**
 * Hook for handling audio file uploads with automatic metadata extraction
 *
 * @example
 * const { upload, isLoading, error } = useAudioUpload({
 *   onSuccess: (result) => {
 *     console.log('Uploaded:', result.fileName);
 *     console.log('Duration:', result.metadata.duration);
 *   },
 *   onError: (err) => console.error(err),
 *   maxFileSize: 50 * 1024 * 1024, // 50MB
 * });
 *
 * const handleFileInput = async (e: React.ChangeEvent<HTMLInputElement>) => {
 *   const file = e.target.files?.[0];
 *   if (file) await upload(file);
 * };
 */
export function useAudioUpload(options: UseAudioUploadOptions = {}): UseAudioUploadReturn {
  const { onError, onSuccess, validateFile, maxFileSize = DEFAULT_MAX_FILE_SIZE } = options;

  const { error, reset, extract, isLoading } = useAudioMetadata();

  const upload = useCallback(
    async (file: File): Promise<AudioUploadResult | null> => {
      // Validate file type
      if (!isAudioBlob(file)) {
        const errorMsg = 'Please select an audio file';
        onError?.(errorMsg);
        return null;
      }

      // Validate file size
      if (file.size > maxFileSize) {
        const maxSizeMB = Math.floor(maxFileSize / 1024 / 1024);
        const errorMsg = `File size exceeds ${maxSizeMB}MB limit`;
        onError?.(errorMsg);
        return null;
      }

      // Custom validation
      if (validateFile) {
        const validationError = validateFile(file);
        if (validationError) {
          onError?.(validationError);
          return null;
        }
      }

      // Extract metadata
      const result = await extract(file);

      if (!result) {
        onError?.(error || 'Failed to process audio file');
        return null;
      }

      const uploadResult: AudioUploadResult = {
        ...result,
        fileName: file.name,
        fileSize: file.size,
      };

      onSuccess?.(uploadResult);
      return uploadResult;
    },
    [extract, error, maxFileSize, onError, onSuccess, validateFile],
  );

  return {
    error,
    reset,
    upload,
    isLoading,
  };
}
